import React, { useEffect, useRef } from 'react';
import { generatePath, Redirect, Route, useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { organizationFetch, organizationSelect } from 'appRedux/actions';
import { actions as divisionActions } from 'packages/divisions';

import RemoteConfigGate from 'components/RemoteConfigGate';
import {
  REMOTE_CONFIG_KEYS,
  USER_ROLES,
  canGetOrganizationDocument,
  hasAnyAllowedRole,
  hasOrgMembership,
  isPackedClaims,
  resolveOrgAccess,
  useAccountWritable,
} from 'packages/utils';

import AccessDeniedRedirect from './AccessDeniedRedirect';

const AuthRoute = ({
  exact = false,
  path,
  location,
  component: Component,
  allowedRoles = null,
  onOrgChangeRedirectTo,
  remoteConfigKey,
  writeOnly = false,
}) => {
  const { orgId: paramOrgId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const loadingAccess = useSelector(({ user }) => user.access.loading);
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUserLoaded = useSelector(({ user }) => user.orgUser?.loaded);
  const orgUserAccess = useSelector(({ user }) => user.orgUser.data?.access);
  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions) || {};
  const selectedOrgId = useSelector(({ organizations }) => organizations?.organization?.id);
  const profileOrganizations = useSelector(({ user }) => user.profile?.data?.organizations);
  const profileLoaded = useSelector(({ user }) => user.profile?.loaded);
  const assignedOrgId = useSelector(({ divisions }) => divisions?.assigned?.data?.orgId);
  const assignedLoaded = useSelector(({ divisions }) => divisions?.assigned?.loaded);
  const initialOrgSyncDone = useRef(false);
  const syncingOrg = useRef(null);
  const claimsReady = Boolean(claims);
  const packedClaims = isPackedClaims(claims);
  const canAccessParamOrg = hasOrgMembership(claims, profileOrganizations, paramOrgId);
  const canFetchParamOrg = canGetOrganizationDocument(claims, paramOrgId);
  const waitingForMembership = packedClaims && !profileLoaded && !canAccessParamOrg;
  const organizationMismatch = Boolean(
    onOrgChangeRedirectTo && paramOrgId && paramOrgId !== selectedOrgId,
  );
  const rejectUrlOrganization = !initialOrgSyncDone.current && claimsReady && !loadingAccess &&
    organizationMismatch && !waitingForMembership && !canAccessParamOrg;
  const orgDataReady =
    assignedLoaded && assignedOrgId === selectedOrgId && (!packedClaims || orgUserLoaded);

  // Packed-token fallback: the org user doc in redux belongs to the selected org, so it is
  // only a valid fallback when the route param points at that same org.
  const fallbackOrgAccess = paramOrgId === selectedOrgId ? orgUserAccess : undefined;
  const userOrgAccess = resolveOrgAccess(claims, paramOrgId, fallbackOrgAccess);

  const hasAccess = hasAnyAllowedRole(allowedRoles, userOrgAccess, userOrgDivisions);
  // Pure "create" pages close while the account is read-only. Edit routes stay open on purpose:
  // they double as detail views. Their save action must be hidden by the screen itself, through
  // a write `allowedRole` or `useAccountWritable()`: this route check ignores the account status.
  const accountWritable = useAccountWritable();

  useEffect(() => {
    if (
      !onOrgChangeRedirectTo || !selectedOrgId || loadingAccess || !claimsReady ||
      rejectUrlOrganization
    ) {
      return;
    }
    if (!initialOrgSyncDone.current) {
      if (paramOrgId !== selectedOrgId && waitingForMembership) {
        return;
      }
      initialOrgSyncDone.current = true;
      if (paramOrgId && paramOrgId !== selectedOrgId && canAccessParamOrg) {
        // URL adoption belongs here: drawer navigation may never mount the org selector.
        syncingOrg.current = { id: paramOrgId, selectionObserved: false };
        dispatch(organizationSelect(paramOrgId));
        if (canFetchParamOrg) {
          dispatch(organizationFetch(paramOrgId));
        }
        dispatch(divisionActions.assignedDivisionsFetch(paramOrgId));
        return;
      }
    }
    // Strict Mode may replay this effect before Redux's new selection is rendered.
    if (
      syncingOrg.current && !syncingOrg.current.selectionObserved &&
      selectedOrgId !== syncingOrg.current.id
    ) {
      return;
    }
    if (paramOrgId !== selectedOrgId) {
      syncingOrg.current = null;
      history.replace(generatePath(onOrgChangeRedirectTo, { orgId: selectedOrgId }));
    } else if (syncingOrg.current) {
      if (orgDataReady) {
        syncingOrg.current = null;
      } else {
        syncingOrg.current.selectionObserved = true;
      }
    }
  }, [
    canAccessParamOrg,
    canFetchParamOrg,
    claimsReady,
    dispatch,
    history,
    loadingAccess,
    onOrgChangeRedirectTo,
    orgDataReady,
    paramOrgId,
    rejectUrlOrganization,
    selectedOrgId,
    waitingForMembership,
  ]);

  const renderRoute = routeProps => {
    // Wait for claims before mounting the screen or RemoteConfigGate so RC
    // cannot briefly show a gated view while roles are still loading.
    // Packed tokens have no org role until the org user document reloads after a
    // reset. Rendering nothing avoids sending the user to / during that read.
    // An inaccessible deep link must never be rewritten into a different org's report.
    // Keep this before the mismatch/loading gate: no org switch is needed to reject it.
    if (rejectUrlOrganization) {
      return <AccessDeniedRedirect hasOrgAccess={false} />;
    }
    if (
      loadingAccess ||
      organizationMismatch ||
      (syncingOrg.current && !orgDataReady) ||
      (packedClaims && orgUserLoaded === false)
    ) {
      return null;
    }
    if (!hasAccess) {
      // Without an org in the URL there is no org to blame, so the role message applies.
      const hasOrgAccess =
        !paramOrgId || hasOrgMembership(claims, profileOrganizations, paramOrgId);
      return <AccessDeniedRedirect hasOrgAccess={hasOrgAccess} />;
    }
    if (writeOnly && !accountWritable) {
      const listPath =
        paramOrgId && onOrgChangeRedirectTo
          ? generatePath(onOrgChangeRedirectTo, { orgId: paramOrgId })
          : '/';
      return <Redirect to={listPath} />;
    }
    const content = <Component {...routeProps} />;
    if (!remoteConfigKey) {
      return content;
    }
    return <RemoteConfigGate configKey={remoteConfigKey}>{content}</RemoteConfigGate>;
  };

  return <Route exact={exact} location={location} path={path} render={renderRoute} />;
};

AuthRoute.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  path: PropTypes.string.isRequired,
  location: PropTypes.object,
  onOrgChangeRedirectTo: PropTypes.string.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOf(Object.values(USER_ROLES))),
  exact: PropTypes.bool,
  remoteConfigKey: PropTypes.oneOf(Object.values(REMOTE_CONFIG_KEYS)),
  writeOnly: PropTypes.bool,
};

export default React.memo(AuthRoute);
